/**
 * Anzeigehilfen, die nur die Web-App betreffen.
 *
 * Alles, was Web und Mobile gemeinsam brauchen, gehört nach `@hausmeter/i18n` —
 * insbesondere Einheiten und Zahlen. Hier steht nur, was an dieser Oberfläche hängt.
 */

/**
 * Laufzeit als grobe Angabe. Sekundengenau ist sie nicht interessant: Die Frage
 * dahinter lautet „läuft der Dienst seit gestern oder seit zwei Minuten", und die
 * beantwortet eine Zahl mit Einheit besser als eine Uhrzeit.
 *
 * **Ab einer Stunde stehen zwei Einheiten da**, und das ist keine Genauigkeit um
 * ihrer selbst willen: Zwischen „2 h" und „2 h" liegt fast eine ganze Stunde, in
 * der man an einem frisch neu gestarteten Gerät nicht erkennt, ob es sich gerade
 * eben oder vor 59 Minuten gemeldet hat. Die zweite Einheit ist immer die
 * nächstkleinere — Tage mit Stunden, Stunden mit Minuten; darunter bleibt es bei
 * einer, weil „45 min 12 s" eine Genauigkeit vorspiegelte, die eine alle fünf
 * Minuten gemeldete Zahl nicht hat.
 *
 * **Ein glatter Rest fällt weg.** „2 h 0 min" liest sich wie ein Fehler, und die
 * Angabe ist dieselbe.
 */
export function formatUptime(seconds: number): string {
  if (seconds < 0) return "–";
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3_600) return `${Math.floor(seconds / 60)} min`;

  if (seconds < 86_400) {
    const hours = Math.floor(seconds / 3_600);
    const minutes = Math.floor((seconds % 3_600) / 60);
    return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
  }

  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  return hours === 0 ? `${days} d` : `${days} d ${hours} h`;
}

/**
 * Ein Zeitstempel aus der API als Millisekunden seit der Epoche.
 *
 * **Warum nicht `Date.parse`:** Das ist projektweit gesperrt, weil es bei allem
 * außer ISO-8601 implementierungsabhängig rät — und ein falsch gedeuteter
 * Zeitpunkt fällt niemandem auf. Hier wird deshalb genau das eine Format
 * zerlegt, das `TimestampSchema` im Vertrag zusichert, und alles andere als
 * ungültig zurückgewiesen (Regel 9: was unbekannt ist, wird abgelehnt).
 *
 * Zeitzonen kommen dabei nicht vor: Der Zeitstempel ist absolut. Zonen betreffen
 * die Frage, wo ein *Tag* anfängt — dafür gibt es `@hausmeter/timeseries`, und
 * nichts davon wird hier gebraucht.
 */
const ISO_INSTANT =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(?:Z|([+-])(\d{2}):(\d{2}))$/;

/**
 * Monat und Jahr eines Zeitpunkts — „März 2024".
 *
 * **In UTC formatiert**, nicht in der Zone des Browsers. Ein Konto, das am
 * Ersten um 00:30 Uhr UTC angelegt wurde, gehörte sonst je nach Aufenthaltsort
 * des Betrachters in den Vormonat. Für „Mitglied seit" ist das folgenlos, für
 * die Nachvollziehbarkeit nicht: Zwei Menschen sollen dieselbe Angabe lesen.
 */
export function formatMonthYear(iso: string): string {
  const millis = timestampMs(iso);
  if (millis === null) return "Zeitpunkt unlesbar";

  return new Intl.DateTimeFormat("de-DE", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(millis));
}

/**
 * Ein Zeitpunkt in Worten, in der Zeitzone des Betrachters — „8. September
 * 2026, 18:36". Für „angemeldet seit" an einer Sitzung (HM-101): Dort steht die
 * Uhrzeit dazu, weil zwei Anmeldungen am selben Tag sonst nicht zu
 * unterscheiden wären, und die Zone des Betrachters, weil er sich selbst
 * angemeldet hat — er weiß, wo er dabei war.
 */
export function formatDateTime(iso: string): string {
  const millis = timestampMs(iso);
  if (millis === null) return "Zeitpunkt unlesbar";

  return new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(millis));
}

/**
 * Exportiert, seit mehr als diese Datei rechnen muss: Die Gruppierung der
 * Datenpunkte vergleicht Messzeitpunkte miteinander (HM-049). Sie soll dafür
 * **nicht** `Date.parse` benutzen — genau die Streuung, gegen die es diese
 * Funktion gibt.
 */
export function timestampMs(iso: string): number | null {
  const match = ISO_INSTANT.exec(iso);
  if (match === null) return null;

  const [, year, month, day, hour, minute, second, millis, sign, offsetH, offsetM] = match;

  const utc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    Number((millis ?? "0").padEnd(3, "0")),
  );

  /**
   * Der Vertrag erlaubt einen Versatz (`z.iso.datetime({ offset: true })`), auch
   * wenn die API heute durchgehend `Z` liefert. Ihn zurückzuweisen hieße, eine
   * vertragsgemäße Antwort als unlesbar zu melden.
   *
   * Das ist **keine** Zeitzonen-Arithmetik: Ein Versatz im Zeitstempel ist Teil
   * der Schreibweise eines absoluten Zeitpunkts. Zonen betreffen die Frage, wo
   * ein Kalendertag anfängt — dafür gibt es `@hausmeter/timeseries`.
   */
  if (sign === undefined) return utc;

  const offset = (Number(offsetH) * 60 + Number(offsetM)) * 60_000;
  return sign === "+" ? utc - offset : utc + offset;
}

/**
 * Wie lange ein Zeitpunkt her ist — „vor 8 Sekunden", „vor 2 Stunden".
 *
 * Die Frage in der Geräteverwaltung lautet nicht „wann genau", sondern „kommt da
 * gerade noch etwas herein". Deshalb grob, in Worten und **ohne** Rückfall auf
 * ein Datum: Ein Datum müsste in der Zeitzone der Anlage stehen, nicht in der
 * des Browsers, und wäre für diese Frage ohnehin die schlechtere Antwort. „Vor
 * 43 Tagen" sagt genau das, worum es geht.
 *
 * `null` heißt: noch nie. Bei einer frisch eingerichteten Integration ist das
 * der Normalfall und kein Fehler — deshalb steht dort ein Satz und kein „–".
 */
/**
 * Ein Kalendertag `YYYY-MM-DD` in Worten — „2. Oktober 2026". Kein Zeitpunkt,
 * keine Zeitzone: Ein Einzugsdatum ist ein Tag, und er wird nicht verschoben.
 */
export function formatDate(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)),
  );
}

export function formatTimeAgo(iso: string | null, now: number = Date.now()): string {
  if (iso === null) return "noch keine Daten";

  const then = timestampMs(iso);
  if (then === null) return "Zeitpunkt unlesbar";

  const seconds = Math.round((now - then) / 1000);
  // Eine Uhr, die vorgeht, ergibt keine Zukunft — sie ergibt „gerade eben".
  if (seconds < 5) return "gerade eben";
  if (seconds < 60) return `vor ${seconds} Sekunden`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `vor ${minutes} ${minutes === 1 ? "Minute" : "Minuten"}`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} ${hours === 1 ? "Stunde" : "Stunden"}`;

  const days = Math.floor(hours / 24);
  return `vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
}

/**
 * Wie jemand angesprochen wird: der Name, sonst die E-Mail-Adresse.
 *
 * **Kein Platzhalter wie „Unbekannt".** Vor- und Nachname sind freiwillig, und
 * der Normalfall ist, dass sie fehlen — dann ist die E-Mail-Adresse nicht der
 * Ersatz für einen Namen, sondern schlicht der Name, unter dem dieses Konto
 * bekannt ist. Auch ein halber Name zählt: Wer nur den Vornamen einträgt, will
 * mit ihm angesprochen werden.
 */
export function displayName(person: {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string;
}): string {
  const name = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
  return name.length > 0 ? name : person.email;
}

/**
 * Die Initialen fürs Profilbild, solange keines hochgeladen ist — „AB".
 *
 * `null`, wenn kein Name da ist. Aus einer E-Mail-Adresse Buchstaben zu ziehen
 * wäre geraten: `info@…` ergäbe ein „I", das über niemanden etwas aussagt. Dann
 * steht dort lieber das gezeichnete Symbol.
 */
export function initialsOf(person: {
  readonly firstName: string | null;
  readonly lastName: string | null;
}): string | null {
  const letters = [person.firstName, person.lastName]
    .filter((part): part is string => Boolean(part))
    .map((part) => part.trim().slice(0, 1).toUpperCase())
    .join("");

  return letters.length > 0 ? letters : null;
}

/** Zustandsstufen des Health-Endpunkts als deutscher Text. */
export const HEALTH_LABELS = {
  ok: "in Ordnung",
  degraded: "eingeschränkt",
  down: "aus",
} as const;

export type HealthState = keyof typeof HEALTH_LABELS;

export function healthLabel(state: string): string {
  return HEALTH_LABELS[state as HealthState] ?? state;
}
